import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import type { PaidNotBilledRow, PaidNotBilledState } from '../../api/types';
import { Button } from '../../components/ui/Button';
import { ConfirmDialog } from '../../components/ui/ConfirmDialog';
import { DataTable, type Column } from '../../components/ui/DataTable';
import { StatusIcon, type StatusIconName } from '../../components/ui/StatusIcon';
import { StatusPill } from '../../components/ui/StatusPill';
import { toast } from '../../components/ui/Toast';
import { mapPaymentError } from '../../api/endpoints/payments';
import { AbsorbChargeDialog, type AbsorbChoice } from './AbsorbChargeDialog';
import { absorbReasonLabel, chargeTypeLabel } from './absorbReason';
import { formatMoney, parsePaise } from './allocation';
import { useAbsorbCharge, useAddChargeToNextInvoice, usePaidNotBilled } from './hooks';
import { OrderLabel } from './OrderLabel';
import { formatIstDayMon } from './paymentDates';
import styles from './Payments.module.css';
import tabStyles from './PaidNotBilledTab.module.css';

const STATE_PILL: Record<
  PaidNotBilledState,
  { label: string; tone: 'red' | 'amber' | 'gray'; icon: StatusIconName }
> = {
  OPEN: { label: 'Not billed', tone: 'red', icon: 'close' },
  ADDED_TO_INVOICE: { label: 'Next invoice', tone: 'amber', icon: 'hourglass' },
  ABSORBED: { label: 'Absorbed', tone: 'gray', icon: 'history' },
};

function StatePill({ state }: { state: PaidNotBilledState }) {
  const pill = STATE_PILL[state] ?? STATE_PILL.OPEN;
  return (
    <StatusPill
      bg={`var(--status-${pill.tone}-bg)`}
      text={`var(--status-${pill.tone}-text)`}
      label={pill.label}
      icon={<StatusIcon name={pill.icon} />}
    />
  );
}

const money = (value: string) => formatMoney(parsePaise(value));
const age = (days: number) => (days <= 0 ? 'Today' : days === 1 ? '1 day ago' : `${days} days ago`);

/** Partner charges DNM paid that no shipper invoice carries yet (#293). Amounts are the server's. */
export function PaidNotBilledTab() {
  const { t } = useTranslation();
  const [showAbsorbed, setShowAbsorbed] = useState(false);
  const query = usePaidNotBilled(showAbsorbed);
  const addToInvoice = useAddChargeToNextInvoice();
  const absorb = useAbsorbCharge();
  const [adding, setAdding] = useState<PaidNotBilledRow | null>(null);
  const [absorbing, setAbsorbing] = useState<PaidNotBilledRow | null>(null);
  const rows = query.data?.rows ?? [];

  const columns: Column<PaidNotBilledRow>[] = [
    {
      key: 'order',
      header: 'Order',
      render: (r) => (
        <>
          <OrderLabel shipmentId={r.shipmentId} />
          <div className={styles.subtle}>{chargeTypeLabel(r.chargeType)}</div>
        </>
      ),
    },
    {
      key: 'parties',
      header: 'Shipper',
      render: (r) => (
        <>
          {r.shipperName}
          <div className={styles.subtle}>{t('payments.paidNotBilledTab.paidTo', { name: r.partnerName })}</div>
        </>
      ),
    },
    {
      key: 'amount',
      header: 'Paid to partner',
      render: (r) => (
        <>
          <span className={styles.mono}>{money(r.partnerAmount)}</span>
          <div className={styles.subtle}>
            {formatIstDayMon(r.paidOn)} · {age(r.ageDays)}
          </div>
        </>
      ),
    },
    {
      key: 'status',
      header: 'Status',
      render: (r) => (
        <>
          <StatePill state={r.state} />
          {r.state === 'ABSORBED' && r.absorbed && (
            <div className={styles.subtle}>
              {t('payments.paidNotBilledTab.absorbedBy', { name: r.absorbed.byName })} · {absorbReasonLabel(r.absorbed.reason)}
              {r.absorbed.note ? ` (${r.absorbed.note})` : ''} · {formatIstDayMon(r.absorbed.at)}
            </div>
          )}
          {r.state === 'OPEN' && (
            <div className={tabStyles.rowActions}>
              <Button
                variant="primary"
                size="sm"
                onClick={() => {
                  addToInvoice.reset();
                  setAdding(r);
                }}
              >
                {t('payments.paidNotBilledTab.addToNextInvoice')}
              </Button>
              <Button
                variant="secondary"
                size="sm"
                onClick={() => {
                  absorb.reset();
                  setAbsorbing(r);
                }}
              >
                {t('payments.paidNotBilledTab.absorb')}
              </Button>
            </div>
          )}
        </>
      ),
    },
  ];

  const confirmAbsorb = (choice: AbsorbChoice) => {
    if (!absorbing) return;
    const row = absorbing;
    absorb.mutate(
      { chargeId: row.chargeId, request: { reason: choice.reason, note: choice.note } },
      {
        onSuccess: () => {
          setAbsorbing(null);
          toast.success(
            `Absorbed ${money(row.partnerAmount)} on #${row.shipmentId}. It won’t be billed.`,
          );
        },
      },
    );
  };

  const confirmAdd = () => {
    if (!adding) return;
    const row = adding;
    addToInvoice.mutate(row.chargeId, {
      onSuccess: () => {
        setAdding(null);
        toast.success(
          `Added ${money(row.partnerAmount)} to the next invoice for ${row.shipperName}.`,
        );
      },
    });
  };

  return (
    <div>
      <div className={styles.sectionTitle}>
        <div>
          <h3>{t('payments.paidNotBilledTab.heading')}</h3>
          <p>{t('payments.paidNotBilledTab.intro')}</p>
        </div>
        <label className={tabStyles.toggle}>
          <input
            type="checkbox"
            checked={showAbsorbed}
            onChange={(e) => setShowAbsorbed(e.target.checked)}
          />
          {t('payments.paidNotBilledTab.showAbsorbed')}
        </label>
      </div>
      <DataTable
        columns={columns}
        rows={rows}
        keyFn={(r) => r.chargeId}
        loading={query.isLoading}
        error={query.isError ? 'Couldn’t load paid, not billed charges' : undefined}
        onRetry={() => query.refetch()}
        empty="Nothing to bill. Every paid partner charge is on a shipper invoice."
      />
      <ConfirmDialog
        isOpen={adding != null}
        title={adding ? `Add ${money(adding.partnerAmount)} to the next invoice` : ''}
        description={
          adding
            ? `${chargeTypeLabel(adding.chargeType)} on #${adding.shipmentId} goes on the next invoice to ${adding.shipperName}.`
            : ''
        }
        confirmText="Add to next invoice"
        variant="primary"
        loading={addToInvoice.isPending}
        errorMessage={addToInvoice.isError ? mapPaymentError(addToInvoice.error) : null}
        onConfirm={confirmAdd}
        onCancel={() => setAdding(null)}
      />
      <AbsorbChargeDialog
        row={absorbing}
        loading={absorb.isPending}
        errorMessage={absorb.isError ? mapPaymentError(absorb.error) : null}
        onConfirm={confirmAbsorb}
        onCancel={() => setAbsorbing(null)}
      />
    </div>
  );
}
